import React, { useState, useEffect } from 'react';
import { Empty, Row, Col, Icon, Radio, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { flexCenterRight } from '../theme';
import { getCurrencySign, getCurrencyFullName } from '../utils/invoices';
import moment from 'moment';
import { getFinancialYearByDate } from './../utils';

import { LineChart, Line, Bar, ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const { sortBy, groupBy, sumBy } = require('lodash');

const calculateYear = (invoice) => {
  if (invoice.actualCurrency === 'GBP') {
    return getFinancialYearByDate(invoice.paymentReceivedDate)
  } else {
    return getFinancialYearByDate(invoice.convertGBPDate)
  }
};

const buildTotalGBP = (yearInvoices, user) => {
  const userInvoices = yearInvoices.filter((i) => i.user_firstname === user);
  const userInvoicesTotalGBP = sumBy(userInvoices.filter((i) => i.actualCurrency === 'GBP'), 'paid');
  const userInvoicesTotalNonGBP = sumBy(userInvoices.filter((i) => i.actualCurrency !== 'GBP'), 'convertPaid');
  return parseFloat((userInvoicesTotalGBP + userInvoicesTotalNonGBP).toFixed(2));
}

const getMonthNumber = (monthName) => {
  switch(monthName) {
    case 'Jan':
      return '1';
    case 'Feb':
      return '2';
    case 'Mar':
      return '3';
    case 'Apr':
      return '4';
    case 'May':
      return '5';
    case 'Jun':
      return '6';
    case 'Jul':
      return '7';
    case 'Aug':
      return '8';
    case 'Sep':
      return '9';
    case 'Oct':
      return '10';
    case 'Nov':
      return '11';
    case 'Dec':
      return '12';
    default:
      return null;
  }
};

const buildChartData = (reportData) => {
  let arr;
  if (reportData.chartData.type === 'year') {
    arr = [
      {month: 'Jan'},
      {month: 'Feb'},
      {month: 'Mar'},
      {month: 'Apr'},
      {month: 'May'},
      {month: 'Jun'},
      {month: 'Jul'},
      {month: 'Aug'},
      {month: 'Sep'},
      {month: 'Oct'},
      {month: 'Nov'},
      {month: 'Dec'},
    ]
  }
  if (reportData.chartData.type === 'financial-year') {
    arr = [
      {month: 'Jan'},
      {month: 'Feb'},
      {month: 'Mar'},
      {month: 'Apr'},
      {month: 'May'},
      {month: 'Jun'},
      {month: 'Jul'},
      {month: 'Aug'},
      {month: 'Sep'},
      {month: 'Oct'},
      {month: 'Nov'},
      {month: 'Dec'},
    ]
  }


  const buildPerson = (name, currencyData, monthNum) => {
    const foundPerson = currencyData.data.find((i) => i.user_firstname === name);
    if (foundPerson) {
      const foundMonth = foundPerson.months.find((i) => i.monthNumber === monthNum);
      if (foundMonth) {
        return foundMonth.total;
      }
    }
    return 0;
  }

  let sgdArr = [];
  const foundSGD = reportData.chartData.data.find((i) => i.currency === 'SGD');
  if (foundSGD) {
    sgdArr = arr.map((i) => {
      return {
        ...i,
        ...{lin: buildPerson('Lin', foundSGD, getMonthNumber(i.month))},
        ...{anny: buildPerson('Anny', foundSGD, getMonthNumber(i.month))},
      }
    });
  }

  let gbpArr = [];
  const foundGBP = reportData.chartData.data.find((i) => i.currency === 'GBP');
  if (foundGBP) {
    gbpArr = arr.map((i) => {
      return {
        ...i,
        ...{lin: buildPerson('Lin', foundGBP, getMonthNumber(i.month))},
        ...{anny: buildPerson('Anny', foundGBP, getMonthNumber(i.month))},
      }
    });
  }

  let hkdArr = [];
  const foundHKD = reportData.chartData.data.find((i) => i.currency === 'HKD');
  if (foundHKD) {
    hkdArr = arr.map((i) => {
      return {
        ...i,
        ...{lin: buildPerson('Lin', foundHKD, getMonthNumber(i.month))},
        ...{anny: buildPerson('Anny', foundHKD, getMonthNumber(i.month))},
      }
    });
  }

  return {
    gbpArr,
    sgdArr,
    hkdArr,
  };
}

const getBase64FromUrl = async (url) => {
  const data = await fetch(url);
  const blob = await data.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    }
  });
}

const buildReportPDFTemplate = async (reportData) => {
  const defaultSize = 11;
  const titleSize = 15;

  const Linelayout = {
    hLineWidth: function(i, node) {
      return (i === 0 || i === node.table.body.length) ? 0 : 1;
    },
    vLineWidth: function(i, node) {
      return 0;
    }
  }

  const imageURL = 'https://link-global-public.s3.eu-west-1.amazonaws.com/Link-Global.jpg';
  const image = await getBase64FromUrl(imageURL);
  const content = [];
  content.push(
    {
      image: image,
      width: 170,
      height: 30,
    },
  );
  content.push(
    {
      text: '\n',
    },
  );
  content.push({
    text: `Paid Report (${reportData.period.start} to ${reportData.period.end})`,
    fontSize: titleSize,
    bold: true,
    alignment: 'center',
    margin: [0, 0, 0, 6],
  });

  if (reportData.data.length === 0) {
    content.push({text: '\n',});
    content.push({
      text: `Not Available`,
      fontSize: 14,
      bold: true,
      alignment: 'center'
    });
  }

  for (const eachUser of reportData.data) {
    const line = {
      table: {
        widths: ['*'],
        body: [[" "], [" "]]
      },
      layout: Linelayout,
    };
    content.push(line);

    content.push({
      text: `${eachUser.user_firstname} ${eachUser.user_lastname}`,
      fontSize: 14,
      bold: true,
    });

    content.push({text: '\n',});

    for (const eachCurrency of eachUser.data) {
      content.push({
        text: `${getCurrencyFullName(eachCurrency.currency)} (${eachCurrency.currency})`,
        fontSize: 13,
        italics: true,
        bold: true,
      });

      content.push({text: '\n',});

      const orderedInvoices = sortBy(eachCurrency.invoices, 'paymentReceivedDate').reverse();
      for (const [index, eachInvoice] of orderedInvoices.entries()) {
        content.push({
          text: `${index+1}. ${moment(eachInvoice.paymentReceivedDate).format('YYYY-MM-DD')}, ${eachInvoice.client_invoice_name}, Candidate: ${eachInvoice.candidate_firstname} ${eachInvoice.candidate_lastname}`,
          fontSize: 12,
        });
        content.push({
          text: `Received: ${getCurrencySign(eachInvoice.currency)} ${eachInvoice.actualFee.toLocaleString()}, Refund: ${getCurrencySign(eachInvoice.currency)} ${eachInvoice.actualRefund.toLocaleString()}`,
          fontSize: 12,
        });
        content.push({
          text: `Paid: ${getCurrencySign(eachInvoice.currency)} ${eachInvoice.paid.toLocaleString()}`,
          fontSize: 12,
          bold: true,
        });
        if (eachInvoice.convertPaid > 0) {
          content.push({
            text: `Converted to GBP: £ ${eachInvoice.convertPaid.toLocaleString()}`,
            fontSize: 10,
            bold: true,
          });
        }
        content.push({text: '\n',});
      }

      content.push({
        table: {
          widths: ['*', '*'],
          body: [
            [
              {
                fontSize: 13, 
                text: `Total ${eachCurrency.currency}: ${getCurrencySign(eachCurrency.currency)} ${eachCurrency.total.toLocaleString()}`, 
                margin: [3, 3, 3, 3], 
                fillColor: '#ddd', 
                bold: true
              },
              {
                fontSize: 10, 
                text: eachCurrency.totalConvert > 0 ? `Converted to GBP: £ ${eachCurrency.totalConvert.toLocaleString()}` : '', 
                margin: [3, 3, 3, 3], 
                fillColor: '#ddd', 
                bold: true
              },
            ]
          ]
        },
        margin: [0, 0, 0, 0],
      });

      content.push({text: '\n',});
    }
  }


  return {
    pageSize: 'A4',
    defaultStyle: {
      fontSize: defaultSize,
    },
    content,
    footer: function (currentPage, pageCount) {
      return { 
        text: "Page " + currentPage.toString() + ' of ' + pageCount + ' (Link Global)', 
        alignment: 'right', 
        fontSize: 10, 
        margin: [0, 20, 20, 0],
        width: '*',
      };
  },
  }
}

function ReportPanel({reportData, loading}) {  
  const [panel, setPanel] = useState('chart');
  const [pdfURL, setPdfURL] = useState(null);

  useEffect(() => {
    async function buildPDF() {
      const template = await buildReportPDFTemplate(reportData);
      pdfMake.createPdf(template).getBuffer((buffer) => {
        const dataUrl = URL.createObjectURL(new Blob([buffer], {
          type: "application/pdf"
        }));
        setPdfURL(dataUrl);
      });
    }
    buildPDF();
  // eslint-disable-next-line
  }, [reportData]);

  const CustomizedAxisTick = props => {
    const { x, y, payload } = props
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="end" fill="#666">{payload.value}</text>
      </g>
    )
  }

  const renderPanel = (reportData) => {
    switch (panel) {
      case 'chart':
        return renderChart(reportData);
      case 'pdf':
        return <div>
          {
            pdfURL !== null ? (
              <iframe
                title="report"
                src={pdfURL}
                style={{ width: '100%', height: '1000px', border: 0 }}
              />
            ) : null
          }
        </div>;
      default:
        return null;
    }
  }

  const renderChart = (reportData) => {
    if (reportData.chartData.type === 'year' || reportData.chartData.type === 'financial-year') {
      const chartData = buildChartData(reportData);
      return (
        <Row>
          <Col span={8}>
            {
              chartData.gbpArr.length > 0 ? (
                <ResponsiveContainer width={'100%'} height={250}>
                  <ComposedChart
                    data={chartData.gbpArr}
                    margin={{
                      top: 5, right: 15, bottom: 5, left: 15,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={<CustomizedAxisTick />} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend content={() => (<center>Great British Pounds</center>)} />
                    <Bar dataKey="lin" barSize={20} fill="#B284BE" />
                    <Bar dataKey="anny" barSize={20} fill="#89CFF0" />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                <span>
                  No Great British Pounds Data
                </span>
              }/>
            }
          </Col>
          <Col span={8}>
            {
              chartData.sgdArr.length > 0 ? (
                <ResponsiveContainer width={'100%'} height={250}>
                  <ComposedChart
                    data={chartData.sgdArr}
                    margin={{
                      top: 5, right: 15, bottom: 5, left: 15,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={<CustomizedAxisTick />} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend content={() => (<center>Singapore Dollars</center>)} />
                    <Bar dataKey="lin" barSize={20} fill="#B284BE" />
                    <Bar dataKey="anny" barSize={20} fill="#89CFF0" />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                <span>
                  No Singapore Dollars Data
                </span>
              }/>
            }
          </Col>
          <Col span={8}>
            {
              chartData.hkdArr.length > 0 ? (
                <ResponsiveContainer width={'100%'} height={250}>
                  <ComposedChart
                    data={chartData.hkdArr}
                    margin={{
                      top: 5, right: 15, bottom: 5, left: 15,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={<CustomizedAxisTick />} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend content={() => (<center>Hong Kong Dollars</center>)} />
                    <Bar dataKey="lin" barSize={20} fill="#B284BE" />
                    <Bar dataKey="anny" barSize={20} fill="#89CFF0" />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                <span>
                  No Hong Kong Dollars Data
                </span>
              }/>
            }
          </Col>
        </Row>
      )
    } else {
      const totalGBPInvoices = reportData.chartData.data.filter((i) => i.actualCurrency === 'GBP' && i.paymentReceivedDate);
      const totalNonGBPInvoices = reportData.chartData.data.filter((i) => i.convertGBPDate);
      const totalInvoices = [...totalGBPInvoices, ...totalNonGBPInvoices].map((i) => {
        return {
          ...i,
          ...{
            customYear: calculateYear(i)
          }
        }
      })

      const groupedTotalInvoices = groupBy(totalInvoices, 'customYear');
      const groupedTotalInvoicesArr = Object.keys(groupedTotalInvoices).map(e => {
        return {
          year: e,
          lin: buildTotalGBP(groupedTotalInvoices[e], 'Lin'),
          anny: buildTotalGBP(groupedTotalInvoices[e], 'Anny')
        }
      });

      return (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart width={500} height={300} data={groupedTotalInvoicesArr}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" padding={{ left: 30, right: 30 }} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="lin" stroke="#8884d8" />
            <Line type="monotone" dataKey="anny" stroke="#82ca9d" />
          </LineChart>
        </ResponsiveContainer>
      )
    }
  }

  return (
    <Spin spinning={loading} indicator={<Icon type="loading" />}>
      <div style={flexCenterRight}>
        <Radio.Group 
          buttonStyle="solid" 
          defaultValue="chart"
          onChange={(e) => {
            setPanel(e.target.value);
          }}
        >
          <Radio.Button value="chart"><FontAwesomeIcon icon={faChartBar} /> Chart</Radio.Button>
          <Radio.Button value="pdf"><FontAwesomeIcon icon={faFilePdf} /> PDF</Radio.Button>
        </Radio.Group>
      </div>
      <br />
      {renderPanel(reportData)}
    </Spin>
  );
}

export default ReportPanel;
